import type { ComponentType, SVGProps } from 'react';

import AskBankiq from '@/assets/icons/nav/ask-bankiq.svg?react';
import Campaigns from '@/assets/icons/nav/campaigns.svg?react';
import CustomerDashboard from '@/assets/icons/nav/customer-dashboard.svg?react';
import Dashboard from '@/assets/icons/nav/dashboard.svg?react';
import Referrals from '@/assets/icons/nav/referrals.svg?react';
import Settings from '@/assets/icons/nav/settings.svg?react';
import Vault from '@/assets/icons/nav/vault.svg?react';

/**
 * Brand icon registry + wrapper (SCRUM-278).
 *
 * These are Ryan Lynch's brand SVGs (sourced verbatim from his prototype
 * `app-shell.html`, the canonical reference — NOT the hash-named extracted
 * package files). Each is imported as a React component via vite-plugin-svgr's
 * `?react` query. The SVGs use `stroke="currentColor"` + `fill="none"`, so the
 * CSS `color` of the surrounding element drives the icon color — identical to
 * how the previous lucide-react icons behaved.
 *
 * `size` is a first-class prop here because every existing call site passed
 * lucide's `size` prop (e.g. `<Icon size={16} />`). SVGR components accept
 * `width`/`height` but NOT `size`, so this wrapper maps `size` → both, making
 * `<Icon name="…" size={16} />` a drop-in replacement.
 *
 * NOTE — lucide-react is still used elsewhere on purpose. Ryan's icon set is
 * task-specific and has no equivalent glyph for several icons the app needs:
 * the `intelligence` and `products` nav items, plus `ChevronDown`, `RefreshCw`,
 * `Info`, `MapPin`, `UserPlus`. Those call sites intentionally keep their lucide
 * icons. A full lucide removal is a follow-on once Ryan supplies the missing
 * glyphs. Do not delete the lucide-react dependency.
 */
const REGISTRY = {
  // The Home Dashboard's 4-panel grid glyph — extracted verbatim from the v7
  // prototype nav (app-shell 7.html; SCRUM-478). Distinct from
  // 'customer-dashboard', which is the Platform Admin console's icon.
  dashboard: Dashboard,
  'customer-dashboard': CustomerDashboard,
  campaigns: Campaigns,
  vault: Vault,
  'ask-bankiq': AskBankiq,
  settings: Settings,
  referrals: Referrals,
} satisfies Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

export type IconName = keyof typeof REGISTRY;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  /** Registered brand icon name. */
  name: IconName;
  /** Square pixel size; mapped to both width and height. Default 16. */
  size?: number;
  /**
   * Accessible label. When provided the icon is exposed as an image with this
   * title; otherwise it is hidden from assistive tech (decorative).
   */
  title?: string;
}

/**
 * Renders a registered brand SVG icon. Keeps each SVG's intrinsic `viewBox`
 * (Ryan deliberately mixes 16×16 / 12×12 and stroke widths 1.25/1.4/1.5); only
 * the outer width/height are set from `size`.
 */
export function Icon({ name, size = 16, title, ...rest }: IconProps) {
  const Svg = REGISTRY[name];
  const a11y = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true };
  return <Svg width={size} height={size} {...a11y} {...rest} />;
}
